/**
 * Servicio de autenticación biométrica
 * Sprint 7 - Login con huella digital en dispositivos móviles
 *
 * Usa @aparajita/capacitor-biometric-auth para el prompt biométrico
 * y @capacitor/preferences para almacenar credenciales de forma segura
 * (SharedPreferences privadas de la app, protegidas por biométrico).
 *
 * Flujo:
 * 1. Tras login exitoso → ofrecer guardar credenciales con biométrico
 * 2. En siguiente apertura → mostrar botón de huella → prompt nativo → auto-login
 */

import { Capacitor } from '@capacitor/core';
import {
    BiometricAuth,
    BiometryType,
    BiometryErrorType,
} from '@aparajita/capacitor-biometric-auth';
import type { CheckBiometryResult, BiometryError } from '@aparajita/capacitor-biometric-auth';
import { Preferences } from '@capacitor/preferences';

// Keys para Preferences
const PREF_BIOMETRIC_ENABLED = 'biometric_enabled';
const PREF_BIOMETRIC_EMAIL = 'biometric_email';
const PREF_BIOMETRIC_TOKEN = 'biometric_token';

export interface BiometricStatus {
    /** El dispositivo soporta biométrico y está disponible */
    isAvailable: boolean;
    /** Tipo de biométrico (fingerprint, faceId, etc.) */
    biometryType: BiometryType;
    /** El usuario ha habilitado login biométrico en la app */
    isEnabled: boolean;
    /** Hay credenciales guardadas */
    hasCredentials: boolean;
}

export interface StoredCredentials {
    email: string;
    token: string;
}

/**
 * Verifica si estamos en plataforma nativa (Android/iOS)
 */
function isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
}

/**
 * Obtiene el nombre legible del tipo de biometría
 */
export function getBiometryName(type: BiometryType): string {
    switch (type) {
        case BiometryType.touchId:
            return 'Touch ID';
        case BiometryType.faceId:
            return 'Face ID';
        case BiometryType.fingerprintAuthentication:
            return 'huella digital';
        case BiometryType.faceAuthentication:
            return 'reconocimiento facial';
        case BiometryType.irisAuthentication:
            return 'escaneo de iris';
        default:
            return 'biométrico';
    }
}

/**
 * Verifica la disponibilidad de biometría en el dispositivo
 */
async function checkBiometry(): Promise<CheckBiometryResult> {
    if (!isNativePlatform()) {
        return {
            isAvailable: false,
            strongBiometryIsAvailable: false,
            biometryType: BiometryType.none,
            biometryTypes: [],
            deviceIsSecure: false,
            reason: 'Not a native platform',
            code: BiometryErrorType.biometryNotAvailable,
            strongReason: '',
            strongCode: BiometryErrorType.biometryNotAvailable,
        };
    }

    return BiometricAuth.checkBiometry();
}

/**
 * Verifica si el usuario tiene biométrico habilitado en la app
 */
async function isBiometricEnabled(): Promise<boolean> {
    const { value } = await Preferences.get({ key: PREF_BIOMETRIC_ENABLED });
    return value === 'true';
}

/**
 * Obtiene el estado completo del biométrico
 */
async function getStatus(): Promise<BiometricStatus> {
    const biometry = await checkBiometry();
    const enabled = await isBiometricEnabled();
    const credentials = await getStoredCredentials();

    return {
        isAvailable: biometry.isAvailable,
        biometryType: biometry.biometryType,
        isEnabled: enabled,
        hasCredentials: credentials !== null,
    };
}

/**
 * Prompt de autenticación biométrica nativa
 * Muestra el diálogo del sistema para que el usuario se autentique
 * @returns true si la autenticación fue exitosa
 */
async function authenticate(): Promise<boolean> {
    try {
        await BiometricAuth.authenticate({
            reason: 'Confirma tu identidad para iniciar sesión',
            cancelTitle: 'Cancelar',
            allowDeviceCredential: true,
            androidTitle: 'Iniciar sesión',
            androidSubtitle: 'Usa tu huella digital para acceder',
            androidConfirmationRequired: false,
        });

        return true;
    } catch (error) {
        const biometryError = error as BiometryError;

        // El usuario canceló - no es un error real
        if (biometryError?.code === BiometryErrorType.userCancel) {
            console.log('[Biometric] User cancelled authentication');
            return false;
        }

        console.warn('[Biometric] Authentication failed:', biometryError?.message);
        return false;
    }
}

/**
 * Guarda credenciales para login biométrico futuro
 * Se llama después de un login exitoso cuando el usuario acepta
 */
async function saveCredentials(email: string, token: string): Promise<void> {
    // Codificar en base64 para ofuscación básica
    // La seguridad real viene del biométrico + sandbox de la app
    const encodedEmail = btoa(email);
    const encodedToken = btoa(token);

    await Preferences.set({ key: PREF_BIOMETRIC_EMAIL, value: encodedEmail });
    await Preferences.set({ key: PREF_BIOMETRIC_TOKEN, value: encodedToken });
    await Preferences.set({ key: PREF_BIOMETRIC_ENABLED, value: 'true' });

    console.log('[Biometric] Credentials saved for:', email);
}

/**
 * Recupera las credenciales guardadas
 * Solo debe llamarse después de autenticación biométrica exitosa
 */
async function getStoredCredentials(): Promise<StoredCredentials | null> {
    const { value: encodedEmail } = await Preferences.get({ key: PREF_BIOMETRIC_EMAIL });
    const { value: encodedToken } = await Preferences.get({ key: PREF_BIOMETRIC_TOKEN });

    if (!encodedEmail || !encodedToken) {
        return null;
    }

    try {
        return {
            email: atob(encodedEmail),
            token: atob(encodedToken),
        };
    } catch {
        console.warn('[Biometric] Failed to decode stored credentials');
        await clearCredentials();
        return null;
    }
}

/**
 * Actualiza el token guardado (ej. después de re-login)
 */
async function updateStoredToken(token: string): Promise<void> {
    const enabled = await isBiometricEnabled();
    if (!enabled) return;

    const encodedToken = btoa(token);
    await Preferences.set({ key: PREF_BIOMETRIC_TOKEN, value: encodedToken });
    console.log('[Biometric] Stored token updated');
}

/**
 * Elimina credenciales y deshabilita login biométrico
 */
async function clearCredentials(): Promise<void> {
    await Preferences.remove({ key: PREF_BIOMETRIC_EMAIL });
    await Preferences.remove({ key: PREF_BIOMETRIC_TOKEN });
    await Preferences.remove({ key: PREF_BIOMETRIC_ENABLED });
    console.log('[Biometric] Credentials cleared');
}

/**
 * Deshabilita biométrico sin borrar credenciales
 */
async function disable(): Promise<void> {
    await Preferences.set({ key: PREF_BIOMETRIC_ENABLED, value: 'false' });
    console.log('[Biometric] Biometric login disabled');
}

export const biometricService = {
    isNativePlatform,
    checkBiometry,
    getStatus,
    authenticate,
    saveCredentials,
    getStoredCredentials,
    updateStoredToken,
    clearCredentials,
    disable,
    isBiometricEnabled,
    getBiometryName,
};
